import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Zap, Trophy, Gauge } from 'lucide-react';

const vehicles = [
  { id: 1, name: 'Lightning Bolt', speed: 95, handling: 70, color: '#FF6B00' },
  { id: 2, name: 'Thunder Racer', speed: 88, handling: 85, color: '#FF1744' },
  { id: 3, name: 'Inferno', speed: 92, handling: 75, color: '#FF3D00' },
  { id: 4, name: 'Arctic Wind', speed: 85, handling: 90, color: '#00BCD4' },
  { id: 5, name: 'Sonic Dash', speed: 98, handling: 65, color: '#FFEB3B' },
];

const tracks = [
  { id: 1, name: 'Desert Storm', difficulty: 'Hard', laps: 3, emoji: '🏜️' },
  { id: 2, name: 'City Streets', difficulty: 'Medium', laps: 5, emoji: '🌃' },
  { id: 3, name: 'Mountain Pass', difficulty: 'Hard', laps: 3, emoji: '⛰️' },
  { id: 4, name: 'Neon Lights', difficulty: 'Medium', laps: 4, emoji: '🌃' },
];

export default function Game() {
  const [selectedVehicle, setSelectedVehicle] = useState<number | null>(null);
  const [selectedTrack, setSelectedTrack] = useState<number | null>(null);
  const [gameActive, setGameActive] = useState(false);
  const [position, setPosition] = useState(50);
  const [speed, setSpeed] = useState(0);
  const [lap, setLap] = useState(1);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const vehicle = vehicles.find(v => v.id === selectedVehicle);
  const track = tracks.find(t => t.id === selectedTrack);
  const canStartRace = selectedVehicle && selectedTrack;

  useEffect(() => {
    if (!gameActive || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;

    const gameLoop = () => {
      // Clear canvas
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw road
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw road lanes
      ctx.strokeStyle = '#fff59d';
      ctx.setLineDash([20, 10]);
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(canvas.width / 2, 0);
      ctx.lineTo(canvas.width / 2, canvas.height);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw car
      ctx.fillStyle = vehicle?.color || '#FF6B00';
      const carWidth = 30;
      const carHeight = 50;
      ctx.fillRect(position - carWidth / 2, canvas.height - 80, carWidth, carHeight);

      // Draw speedometer
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 14px Arial';
      ctx.fillText(`Speed: ${Math.round(speed)} km/h`, 10, 30);
      ctx.fillText(`Lap: ${lap}/${track?.laps}`, 10, 50);

      animationId = requestAnimationFrame(gameLoop);
    };

    gameLoop();
    return () => cancelAnimationFrame(animationId);
  }, [gameActive, position, speed, lap, vehicle?.color, track?.laps]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!gameActive) return;
    if (e.key === 'ArrowLeft') setPosition(prev => Math.max(30, prev - 10));
    if (e.key === 'ArrowRight') setPosition(prev => Math.min(770, prev + 10));
    if (e.key === ' ') setSpeed(prev => Math.min(200, prev + 20));
  };

  const handleKeyUp = () => {
    setSpeed(prev => Math.max(0, prev - 5));
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameActive]);

  const startRace = () => {
    setGameActive(true);
    setPosition(400);
    setSpeed(0);
    setLap(1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-400 to-cyan-400 bg-clip-text text-transparent">
            🏎️ EXTREME RACING
          </h1>
          <p className="text-slate-400 mt-2">Master the speed. Conquer the tracks.</p>
        </div>
      </header>

      {gameActive ? (
        <div className="w-full h-screen flex flex-col">
          <canvas
            ref={canvasRef}
            width={800}
            height={600}
            className="w-full flex-1 bg-slate-800"
          />
          <div className="bg-slate-900 border-t border-slate-800 p-4">
            <p className="text-slate-400 text-center mb-2">Arrow Keys to Move • Space to Accelerate • Complete {track?.laps} laps</p>
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => {
                  setGameActive(false);
                  setPosition(50);
                  setSpeed(0);
                }}
                className="bg-red-600 hover:bg-red-700"
              >
                Exit Race
              </Button>
              <Button onClick={() => setLap(prev => prev + 1)} className="bg-cyan-600 hover:bg-cyan-700">
                Next Lap
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <main className="container mx-auto px-4 py-12 space-y-12">
          {/* Vehicle Selection */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Zap className="w-6 h-6 text-orange-400" />
              Select Your Vehicle
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {vehicles.map(v => (
                <button
                  key={v.id}
                  onClick={() => setSelectedVehicle(v.id)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    selectedVehicle === v.id
                      ? 'border-orange-400 bg-slate-800 shadow-lg shadow-orange-400/50'
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                  }`}
                >
                  <div
                    className="h-12 rounded mb-3 flex items-center justify-center text-2xl"
                    style={{ backgroundColor: v.color }}
                  >
                    🏎️
                  </div>
                  <h3 className="font-bold text-white text-sm">{v.name}</h3>
                  <div className="text-xs text-slate-400 mt-2">
                    <p>Speed: {v.speed}</p>
                    <p>Handling: {v.handling}</p>
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* Track Selection */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-orange-400" />
              Choose Your Track
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {tracks.map(t => (
                <Card
                  key={t.id}
                  className={`p-6 cursor-pointer transition-all border-2 ${
                    selectedTrack === t.id
                      ? 'border-orange-400 bg-slate-800'
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                  }`}
                  onClick={() => setSelectedTrack(t.id)}
                >
                  <div className="text-5xl mb-3">{t.emoji}</div>
                  <h3 className="font-bold text-white">{t.name}</h3>
                  <Badge variant="secondary" className="mt-3 mb-2">{t.difficulty}</Badge>
                  <p className="text-sm text-slate-400">{t.laps} laps</p>
                </Card>
              ))}
            </div>
          </section>

          {/* Race Preview */}
          {canStartRace && (
            <section className="bg-gradient-to-r from-slate-800 to-slate-800 border border-slate-700 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <Gauge className="w-6 h-6 text-orange-400" />
                Race Ready
              </h2>

              <div className="grid grid-cols-3 gap-8 mb-8">
                <div className="text-center">
                  <div className="h-32 rounded-lg bg-slate-700 mb-3 flex items-center justify-center text-5xl" style={{ backgroundColor: vehicle?.color }}>
                    🏎️
                  </div>
                  <h3 className="font-bold text-white text-lg">{vehicle?.name}</h3>
                  <p className="text-sm text-slate-400 mt-2">Speed: {vehicle?.speed}</p>
                </div>

                <div className="flex flex-col items-center justify-center">
                  <div className="text-6xl font-bold text-slate-600 mb-4">VS</div>
                  <div className="text-center">
                    <p className="text-slate-400">Track</p>
                    <p className="text-2xl text-white font-bold">{track?.emoji}</p>
                    <p className="text-sm text-slate-300">{track?.name}</p>
                  </div>
                </div>

                <div className="text-center">
                  <div className="h-32 rounded-lg bg-slate-700 mb-3 flex items-center justify-center text-5xl">
                    🏁
                  </div>
                  <h3 className="font-bold text-white text-lg">Finish Line</h3>
                  <p className="text-sm text-slate-400 mt-2">{track?.laps} Laps</p>
                </div>
              </div>

              <Button
                onClick={startRace}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold text-lg py-6"
              >
                <Zap className="w-6 h-6 mr-2" />
                START RACE
              </Button>
            </section>
          )}
        </main>
      )}
    </div>
  );
}